<template>
  <div class="diagnostico-fiscal-relatorio-wrapper">
    <!-- ============================================================ -->
    <!-- CABEÇALHO                                                     -->
    <!-- ============================================================ -->
    <header class="relatorio-header">
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">
          <i class="bi bi-shield-check me-2"></i> Diagnóstico de Conformidade Fiscal
        </h4>
        <span class="titulo-acao">
          {{ modoMultiEmpresa ? 'Upgrade em lote — plano' : 'Upgrade individual' }}
          {{ nivelAntigo ? `: ${nivelAntigo} → ${nivelNovo}` : '' }}
        </span>
      </div>

      <div class="info-registro">
        <div class="info-item">
          <strong>Status:</strong>
          <span class="info-valor" :class="podeFazerUpgrade ? 'text-success' : 'text-danger'">
            <i :class="podeFazerUpgrade ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'" class="me-1"></i>
            {{ podeFazerUpgrade ? 'Upgrade Permitido' : 'Upgrade Bloqueado' }}
          </span>
        </div>
        <div class="info-item" v-if="totalPendencias >= 0">
          <strong>Total Pendências:</strong>
          <span class="info-valor">{{ totalPendencias }}</span>
        </div>
      </div>

      <div class="botoes-wrapper">
        <button type="button" class="btn btn-outline-light btn-sm me-2" @click="gerarPDF" :disabled="gerandoPdf">
          <span v-if="gerandoPdf" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-file-earmark-pdf me-1"></i>
          Exportar PDF
        </button>
        <button type="button" class="btn btn-outline-light btn-sm" @click="handleFechar">
          <i class="bi bi-x-lg me-1"></i> Fechar
        </button>
      </div>
    </header>

    <!-- ============================================================ -->
    <!-- CONTEÚDO DO RELATÓRIO (container para PDF)                   -->
    <!-- ============================================================ -->
    <div class="relatorio-body" ref="relatorioContainerRef">
      <!-- Loading -->
      <div v-if="isLoading" class="text-center p-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-2 text-muted">Executando diagnóstico de conformidade...</p>
      </div>

      <!-- Erro -->
      <div v-else-if="erroInterno" class="alert alert-danger d-flex align-items-center gap-3 mb-4">
        <i class="bi bi-exclamation-triangle-fill fs-3"></i>
        <div>
          <strong>Erro ao executar diagnóstico.</strong><br>
          {{ erroInterno }}
        </div>
      </div>

      <template v-else-if="!isLoading">
        <!-- Alerta de bloqueio -->
        <div v-if="!podeFazerUpgrade" class="alert alert-danger d-flex align-items-center gap-3 mb-4">
          <i class="bi bi-exclamation-triangle-fill fs-3"></i>
          <div>
            <strong>Upgrade bloqueado.</strong>
            Resolva as pendências cadastrais abaixo antes de alterar o plano.
          </div>
        </div>
        <div v-else class="alert alert-success d-flex align-items-center gap-3 mb-4">
          <i class="bi bi-check-circle-fill fs-3"></i>
          <div>
            <strong>Nenhuma pendência encontrada.</strong>
            O upgrade pode ser realizado com segurança.
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="resumo-executivo-row mb-4">
          <div class="resumo-card">
            <span class="resumo-label">Total Pendências</span>
            <span class="campo-valor destaque" :class="totalPendencias > 0 ? 'text-danger' : 'text-success'">
              {{ totalPendencias }}
            </span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Sem IBGE</span>
            <span class="campo-valor destaque">{{ pendenciasPorTipo('Parceiro').length }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Sem NCM</span>
            <span class="campo-valor destaque">{{ pendenciasPorTipo('Produto').length }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Sem NBS/LC116</span>
            <span class="campo-valor destaque">{{ pendenciasPorTipo('Servico').length }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Sem Categ. Financeira</span>
            <span class="campo-valor destaque">{{ pendenciasPorTipo('CategoriaFinanceira').length }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Empresas Afetadas</span>
            <span class="campo-valor destaque">{{ empresasComPendencias.length }}</span>
          </div>
        </div>

        <!-- Modo Multi-Empresa: agrupar por empresa -->
        <template v-if="modoMultiEmpresa">
          <div
            v-for="empresa in empresasComPendencias"
            :key="empresa.idEmpresa"
            class="card mb-4"
          >
            <div
              class="card-header d-flex justify-content-between align-items-center cursor-pointer"
              @click="toggleEmpresa(empresa.idEmpresa)"
            >
              <h5 class="mb-0">
                <i :class="expandedEmpresas[empresa.idEmpresa] ? 'bi-chevron-down' : 'bi-chevron-right'" class="me-2"></i>
                {{ empresa.nomeEmpresa || empresa.empresa?.nomeEmpresa }}
              </h5>
              <span class="badge bg-danger">{{ empresa.totalPendencias }} pendência(s)</span>
            </div>
            <div v-if="expandedEmpresas[empresa.idEmpresa]" class="card-body">
              <div v-if="empresa.pendencias && empresa.pendencias.length > 0" class="admin-table-container border-0 shadow-none">
                <table class="table admin-table table-sm">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Entidade</th>
                      <th>Campo Faltante</th>
                      <th>Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, i) in empresa.pendencias" :key="i">
                      <td><span class="badge" :class="badgeClasse(p.tipo)">{{ p.tipo }}</span></td>
                      <td class="fw-medium">{{ p.entidade }}</td>
                      <td><code>{{ p.campoFaltante }}</code></td>
                      <td class="text-muted small">{{ p.descricao }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center text-muted py-3">
                <i class="bi bi-check-circle text-success me-1"></i> Nenhuma pendência detalhada.
              </div>
            </div>
          </div>
        </template>

        <!-- Modo Single-Empresa: agrupar por tipo -->
        <template v-else>
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-list-check me-2 text-primary"></i> Pendências por Categoria</h5>
            </div>
            <div class="card-body">
              <!-- Parceiros -->
              <div v-if="pendenciasPorTipo('Parceiro').length" class="mb-4">
                <h6 class="border-bottom pb-2 mb-3 text-warning">
                  <i class="bi bi-people me-2"></i> Parceiros ({{ pendenciasPorTipo('Parceiro').length }})
                </h6>
                <div class="admin-table-container border-0 shadow-none">
                  <table class="table admin-table table-sm">
                    <thead>
                      <tr>
                        <th>Entidade</th>
                        <th>Campo Faltante</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in pendenciasPorTipo('Parceiro')" :key="i">
                        <td class="fw-medium">{{ p.entidade }}</td>
                        <td><code>{{ p.campoFaltante }}</code></td>
                        <td class="text-muted small">{{ p.descricao }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Produtos -->
              <div v-if="pendenciasPorTipo('Produto').length" class="mb-4">
                <h6 class="border-bottom pb-2 mb-3 text-warning">
                  <i class="bi bi-box me-2"></i> Produtos ({{ pendenciasPorTipo('Produto').length }})
                </h6>
                <div class="admin-table-container border-0 shadow-none">
                  <table class="table admin-table table-sm">
                    <thead>
                      <tr>
                        <th>Entidade</th>
                        <th>Campo Faltante</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in pendenciasPorTipo('Produto')" :key="i">
                        <td class="fw-medium">{{ p.entidade }}</td>
                        <td><code>{{ p.campoFaltante }}</code></td>
                        <td class="text-muted small">{{ p.descricao }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Serviços -->
              <div v-if="pendenciasPorTipo('Servico').length" class="mb-4">
                <h6 class="border-bottom pb-2 mb-3 text-warning">
                  <i class="bi bi-gear me-2"></i> Serviços ({{ pendenciasPorTipo('Servico').length }})
                </h6>
                <div class="admin-table-container border-0 shadow-none">
                  <table class="table admin-table table-sm">
                    <thead>
                      <tr>
                        <th>Entidade</th>
                        <th>Campo Faltante</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in pendenciasPorTipo('Servico')" :key="i">
                        <td class="fw-medium">{{ p.entidade }}</td>
                        <td><code>{{ p.campoFaltante }}</code></td>
                        <td class="text-muted small">{{ p.descricao }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Categorias Financeiras -->
              <div v-if="pendenciasPorTipo('CategoriaFinanceira').length" class="mb-4">
                <h6 class="border-bottom pb-2 mb-3 text-danger">
                  <i class="bi bi-tags me-2"></i> Categorias Financeiras ({{ pendenciasPorTipo('CategoriaFinanceira').length }})
                </h6>
                <div class="admin-table-container border-0 shadow-none">
                  <table class="table admin-table table-sm">
                    <thead>
                      <tr>
                        <th>Parâmetro</th>
                        <th>Campo Faltante</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, i) in pendenciasPorTipo('CategoriaFinanceira')" :key="i">
                        <td class="fw-medium">{{ p.entidade }}</td>
                        <td><code>{{ p.campoFaltante }}</code></td>
                        <td class="text-muted small">{{ p.descricao }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Mensagem se não houver pendências -->
              <div v-if="pendencias.length === 0" class="text-center text-muted py-4">
                <i class="bi bi-emoji-smile fs-1 d-block mb-2"></i>
                <p class="mb-0">Nenhuma pendência cadastral encontrada.</p>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { imprimirPagedJs } from '@/services/relatorioPrintService'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

const emit = defineEmits(['fechar', 'show-notification'])
const authStore = useAuthStore()

const props = defineProps({
  modoMultiEmpresa: { type: Boolean, default: false },
  nivelAntigo: { type: String, default: '' },
  nivelNovo: { type: String, default: '' },
  empresasComPendencias: { type: Array, default: () => [] },
  pendencias: { type: Array, default: () => [] },
  podeFazerUpgrade: { type: Boolean, default: false },
  totalPendencias: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false }
})

const relatorioContainerRef = ref(null)
const gerandoPdf = ref(false)
const expandedEmpresas = reactive({})

// --- Estado interno para operação autônoma ---
const dadosInternos = ref(null)
const carregandoInterno = ref(false)
const erroInterno = ref('')

const modoAutonomo = computed(() => {
  // Se não recebeu dados via props, opera de forma autônoma
  return props.pendencias.length === 0 && props.empresasComPendencias.length === 0
})

const pendencias = computed(() => {
  if (dadosInternos.value) {
    return dadosInternos.value.pendencias || []
  }
  if (props.modoMultiEmpresa) {
    return props.empresasComPendencias.flatMap(e => e.pendencias || [])
  }
  return props.pendencias
})

const totalPendencias = computed(() => {
  if (dadosInternos.value) return dadosInternos.value.totalPendencias
  return props.totalPendencias
})

const podeFazerUpgrade = computed(() => {
  if (dadosInternos.value) return dadosInternos.value.podeFazerUpgrade
  return props.podeFazerUpgrade
})

const isLoading = computed(() => {
  if (modoAutonomo.value) return carregandoInterno.value
  return props.isLoading
})

const pendenciasPorTipo = (tipo) => {
  return pendencias.value.filter(p => p.tipo === tipo)
}

const badgeClasse = (tipo) => {
  if (tipo === 'Parceiro') return 'bg-warning-subtle text-warning-emphasis'
  if (tipo === 'Produto') return 'bg-info-subtle text-info-emphasis'
  if (tipo === 'Servico') return 'bg-secondary-subtle text-secondary-emphasis'
  if (tipo === 'CategoriaFinanceira') return 'bg-danger-subtle text-danger-emphasis'
  return 'bg-secondary-subtle text-secondary-emphasis'
}

const toggleEmpresa = (id) => {
  expandedEmpresas[id] = !expandedEmpresas[id]
}

const gerarPDF = async () => {
  if (!relatorioContainerRef.value) return
  gerandoPdf.value = true
  try {
    await imprimirPagedJs(relatorioContainerRef.value, {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      arquivo: `diagnostico-fiscal-${new Date().toISOString().split('T')[0]}`
    })
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    emit('show-notification', { message: 'Erro ao gerar o PDF do diagnóstico.', type: 'error' })
  } finally {
    gerandoPdf.value = false
  }
}

const handleFechar = () => {
  emit('fechar')
}

const carregarDiagnostico = async () => {
  const idEmpresa = authStore.empresaAtiva
  if (!idEmpresa) {
    erroInterno.value = 'Nenhuma empresa selecionada. Selecione uma empresa no topo da tela.'
    carregandoInterno.value = false
    return
  }

  carregandoInterno.value = true
  erroInterno.value = ''

  try {
    const response = await api.get(`/Fiscal/diagnostico/${idEmpresa}`)
    dadosInternos.value = response.data
  } catch (error) {
    console.error('Erro ao carregar diagnóstico:', error)
    erroInterno.value = error.response?.data?.erro || 'Erro ao executar diagnóstico de conformidade fiscal.'
  } finally {
    carregandoInterno.value = false
  }
}

// Expandir todas as empresas por padrão no modo multi-empresa
onMounted(() => {
  if (modoAutonomo.value) {
    carregarDiagnostico()
  }

  if (props.modoMultiEmpresa) {
    props.empresasComPendencias.forEach(emp => {
      const id = emp.idEmpresa || emp.empresa?.idEmpresa
      if (id) expandedEmpresas[id] = true
    })
  }
})
</script>

<style scoped>
/* ============================================================ */
/* LAYOUT GERAL                                                 */
/* ============================================================ */
.diagnostico-fiscal-relatorio-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--bs-body-bg);
}

.relatorio-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* ============================================================ */
/* CABEÇALHO                                                     */
/* ============================================================ */
.relatorio-header {
  display: flex;
  height: 100px;
  align-items: center;
  gap: 60px;
  padding: 1.5rem;
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
}

.titulo-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.titulo-principal {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
  color: #FFFFFF;
}

.titulo-acao {
  font-size: 0.875rem;
  color: #ADB5BD;
}

.info-registro {
  font-size: 0.9rem;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.info-item strong {
  color: #ADB5BD;
  margin-right: 0.25rem;
}

.info-valor {
  color: #ca8019;
  font-weight: 600;
}

.botoes-wrapper {
  margin-left: auto;
  flex-shrink: 0;
}

.botoes-wrapper .btn {
  color: #fff;
  border-color: rgba(255,255,255,0.3);
}

.botoes-wrapper .btn:hover {
  background-color: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.5);
}

/* ============================================================ */
/* RESUMO EXECUTIVO                                              */
/* ============================================================ */
.resumo-executivo-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.resumo-card {
  flex: 1;
  min-width: 120px;
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition: box-shadow 0.2s ease;
}

.resumo-card:hover {
  box-shadow: 0 0.125rem 0.5rem rgba(0,0,0,0.08);
}

.resumo-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.campo-valor {
  font-size: 0.95rem;
  color: var(--bs-body-emphasis);
  font-weight: 500;
  word-break: break-word;
}

.campo-valor.destaque {
  font-size: 1.1rem;
  font-weight: 700;
}

/* ============================================================ */
/* CARDS                                                         */
/* ============================================================ */
.card {
  background-color: var(--bs-tertiary-bg);
  border-radius: 0.375rem;
  border: 1px solid var(--bs-border-color-translucent) !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.03);
}

.card-header {
  background-color: rgba(52, 73, 94, 0.06);
  border-bottom: 1px solid var(--bs-border-color-translucent) !important;
  padding: 0.85rem 1.25rem;
}

.card-header h5 {
  color: var(--bs-heading-color);
  font-weight: 600;
  font-size: 1.1rem;
}

.card-body {
  padding: 1.25rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* ============================================================ */
/* RESPONSIVIDADE                                                */
/* ============================================================ */
@media (max-width: 991.98px) {
  .relatorio-header {
    position: static;
    height: auto;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .titulo-wrapper {
    align-items: center;
    text-align: center;
  }

  .info-registro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.1rem;
    text-align: center;
  }

  .botoes-wrapper {
    margin-left: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .botoes-wrapper .btn {
    width: 100%;
  }

  .resumo-executivo-row {
    flex-direction: column;
  }
}
</style>
